/**
 * Offline-first ICS 214 Unit Log Service
 *
 * Handles caching, offline creation, and syncing of ICS 214 unit logs
 * and activity entries. Uses IndexedDB (Dexie) for local storage and
 * syncs to the server when online.
 */

import { db, CachedUnitLog, CachedActivityEntry } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { ICS214UnitLog, ActivityEntry } from '@/types/ics214';

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

export interface ICS214SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class ICS214OfflineService {
  // ===========================================================================
  // Unit Log Caching
  // ===========================================================================

  /**
   * Cache unit logs from the server for offline access.
   *
   * Conflict resolution:
   * - If local has pending changes, preserve local version
   * - If local is synced and server is newer, update from server
   * - If local is synced and server is same/older, keep local
   */
  async cacheUnitLogs(logs: ICS214UnitLog[], tenantId: string): Promise<number> {
    let cached = 0;

    for (const log of logs) {
      const existing = await db.cachedUnitLogs.get(log.id);

      if (!existing) {
        await db.cachedUnitLogs.put(this.mapUnitLogToCache(log, tenantId));
        cached++;
      } else if (existing.pending_changes) {
        // Local has unsaved changes — record conflict for UI resolution
        await db.cachedUnitLogs.update(existing.id, {
          conflict_state: 'detected',
          server_updated_at: log.updated_at,
          server_snapshot: log as unknown as Record<string, unknown>,
        });
        console.info(
          `[ICS214Offline] Conflict detected for unit log ${log.id} - preserving local changes`
        );
      } else if (
        new Date(log.updated_at) > new Date(existing.updated_at)
      ) {
        // Server version is newer -- update
        await db.cachedUnitLogs.put(this.mapUnitLogToCache(log, tenantId));
        cached++;
      }
    }

    return cached;
  }

  /**
   * Retrieve cached unit logs, optionally filtered by incident.
   */
  async getCachedUnitLogs(incidentId?: string): Promise<CachedUnitLog[]> {
    let results: CachedUnitLog[];

    if (incidentId) {
      results = await db.cachedUnitLogs
        .where('incident_id')
        .equals(incidentId)
        .toArray();
    } else {
      results = await db.cachedUnitLogs.toArray();
    }

    // Sort by updated_at descending
    results.sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

    return results;
  }

  /**
   * Get a single cached unit log by ID.
   */
  async getUnitLogById(id: string): Promise<CachedUnitLog | undefined> {
    let log = await db.cachedUnitLogs.get(id);
    if (!log) {
      log = await db.cachedUnitLogs
        .where('offline_id')
        .equals(id)
        .first();
    }
    return log;
  }

  /**
   * Get pending (unsynced) unit logs.
   */
  async getPendingUnitLogs(): Promise<CachedUnitLog[]> {
    // IndexedDB can't index booleans, and ICS-214 rows are written with a
    // boolean `is_synced`. An index `.equals(0)` silently misses boolean-false
    // rows, so use a loose filter (matches board/fema/pda-zone/coop services).
    // See offline/__tests__/is-synced-indexing.spec.ts.
    return db.cachedUnitLogs
      .filter((l) => !l.is_synced)
      .toArray();
  }

  // ===========================================================================
  // Activity Entry Caching
  // ===========================================================================

  /**
   * Cache activity entries from the server for a given unit log.
   */
  async cacheActivityEntries(
    unitLogId: string,
    entries: ActivityEntry[],
    tenantId: string
  ): Promise<number> {
    let cached = 0;

    for (const entry of entries) {
      const existing = await db.cachedActivityEntries.get(entry.id);

      if (!existing) {
        await db.cachedActivityEntries.put(
          this.mapActivityEntryToCache(entry, unitLogId, tenantId)
        );
        cached++;
      } else if (existing.pending_changes) {
        // Local has unsaved changes -- preserve
        console.info(
          `[ICS214Offline] Conflict for activity entry ${entry.id} - preserving local changes`
        );
      } else if (
        new Date(entry.updated_at) > new Date(existing.updated_at)
      ) {
        await db.cachedActivityEntries.put(
          this.mapActivityEntryToCache(entry, unitLogId, tenantId)
        );
        cached++;
      }
    }

    return cached;
  }

  /**
   * Retrieve cached activity entries for a unit log.
   */
  async getCachedActivityEntries(unitLogId: string): Promise<CachedActivityEntry[]> {
    const results = await db.cachedActivityEntries
      .where('unit_log_id')
      .equals(unitLogId)
      .toArray();

    // Sort by entry_time ascending (chronological)
    results.sort(
      (a, b) => new Date(a.entry_time).getTime() - new Date(b.entry_time).getTime()
    );

    return results;
  }

  /**
   * Get pending (unsynced) activity entries.
   */
  async getPendingActivityEntries(): Promise<CachedActivityEntry[]> {
    // Loose filter — boolean-false rows aren't in the `is_synced` index.
    // See getPendingUnitLogs above and is-synced-indexing.spec.ts.
    return db.cachedActivityEntries
      .filter((e) => !e.is_synced)
      .toArray();
  }

  // ===========================================================================
  // Sync
  // ===========================================================================

  /**
   * Sync all pending unit logs and activity entries to the server,
   * then refresh cache from the server.
   */
  async syncAll(tenantId: string): Promise<ICS214SyncResult> {
    const result: ICS214SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    // Cross-tenant replay guard: never replay a pending record against a
    // different tenant than the one it was created under. The api client injects
    // the *ambient* X-Tenant-ID, so a Tenant-A record replayed while Tenant B is
    // active would POST to B. Defer (leave pending) such records; they sync when
    // their tenant is active again. Records lacking tenant_id are not skipped.
    const activeTenantId = getActiveTenantId();

    // Sync pending unit logs first (activity entries depend on them existing on server)
    const pendingLogs = await this.getPendingUnitLogs();
    for (const log of pendingLogs) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(log as never)) continue;
      if (log.tenant_id != null && activeTenantId != null && log.tenant_id !== activeTenantId) {
        continue;
      }
      try {
        await this.syncUnitLog(log);
        result.synced++;
      } catch (error) {
        result.failed++;
        result.errors.push(
          `Failed to sync unit log "${log.unit_name}": ${(error as Error).message}`
        );
      }
    }

    // Sync pending activity entries after their parent unit logs
    const pendingEntries = await this.getPendingActivityEntries();
    for (const entry of pendingEntries) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(entry as never)) continue;
      if (entry.tenant_id != null && activeTenantId != null && entry.tenant_id !== activeTenantId) {
        continue;
      }
      try {
        await this.syncActivityEntry(entry);
        result.synced++;
      } catch (error) {
        result.failed++;
        result.errors.push(
          `Failed to sync activity entry: ${(error as Error).message}`
        );
      }
    }

    // Refresh cache from server
    try {
      const serverLogs = await api.get<{ data: ICS214UnitLog[] }>('/ics214?limit=100');
      const logs = serverLogs.data || [];
      await this.cacheUnitLogs(logs, tenantId);
    } catch (error) {
      console.warn('[ICS214Offline] Cache refresh after sync failed:', error);
    }

    result.success = result.failed === 0;
    return result;
  }

  // ===========================================================================
  // Clear Cache
  // ===========================================================================

  /**
   * Clear all cached ICS 214 data.
   */
  async clearCache(): Promise<void> {
    await db.cachedUnitLogs.clear();
    await db.cachedActivityEntries.clear();
  }

  /**
   * Clear cached data for a specific tenant, keeping pending changes.
   */
  async clearSyncedCache(tenantId: string): Promise<void> {
    const syncedLogs = await db.cachedUnitLogs
      .where('tenant_id')
      .equals(tenantId)
      .filter(l => l.is_synced && !l.pending_changes)
      .toArray();

    const logIds = syncedLogs.map(l => l.id);
    if (logIds.length > 0) {
      await db.cachedUnitLogs.bulkDelete(logIds);
    }

    const syncedEntries = await db.cachedActivityEntries
      .where('tenant_id')
      .equals(tenantId)
      .filter(e => e.is_synced && !e.pending_changes)
      .toArray();

    const entryIds = syncedEntries.map(e => e.id);
    if (entryIds.length > 0) {
      await db.cachedActivityEntries.bulkDelete(entryIds);
    }
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  private async syncUnitLog(log: CachedUnitLog): Promise<void> {
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(log)) return;
    try {
      // For now, unit logs created offline would need full create support.
      // Mark as synced after pushing to server.
      if (log.offline_id && log.id === log.offline_id) {
        const response = await api.post<{ id?: string; data?: { id: string } }>('/ics214', {
          unit_name: log.unit_name,
          ics_position: log.ics_position,
          home_agency: log.home_agency,
          incident_id: log.incident_id,
          is_non_incident: log.is_non_incident,
          supervisor_id: log.supervisor_id,
          supervisor_name: log.supervisor_name,
        }, sendTenantOf(log));

        const serverId = response.id || response.data?.id || log.id;
        const oldOfflineId = log.id;

        // Atomic offline→server id swap + FK remap of dependent children.
        // Children (activity entries, roster entries) created under the offline
        // unit log still reference the offline id; without remapping, their POST
        // to /ics214/<unit_log_id>/entries would 404 against a stale id.
        await db.transaction(
          'rw',
          db.cachedUnitLogs,
          db.cachedActivityEntries,
          db.cachedRosterEntries,
          async () => {
            await db.cachedUnitLogs.delete(oldOfflineId);
            await db.cachedUnitLogs.put({
              ...log,
              id: serverId,
              is_synced: true,
              synced_at: new Date().toISOString(),
              pending_changes: false,
              last_error: undefined,
            });

            if (serverId !== oldOfflineId) {
              await db.cachedActivityEntries
                .where('unit_log_id')
                .equals(oldOfflineId)
                .modify({ unit_log_id: serverId });
              await db.cachedRosterEntries
                .where('unit_log_id')
                .equals(oldOfflineId)
                .modify({ unit_log_id: serverId });
            }
          }
        );
      } else {
        // Update existing (backend mounts PATCH /ics214/:id, not PUT)
        await api.patch(`/ics214/${log.id}`, {
          unit_name: log.unit_name,
          ics_position: log.ics_position,
          home_agency: log.home_agency,
          supervisor_id: log.supervisor_id,
          supervisor_name: log.supervisor_name,
        }, sendTenantOf(log));

        await db.cachedUnitLogs.update(log.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }
    } catch (error) {
      await db.cachedUnitLogs.update(log.id, {
        last_error: (error as Error).message,
      });
      throw error;
    }
  }

  private async syncActivityEntry(entry: CachedActivityEntry): Promise<void> {
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(entry)) return;
    try {
      if (entry.offline_id && entry.id === entry.offline_id) {
        const response = await api.post<{ id?: string; data?: { id: string } }>(
          `/ics214/${entry.unit_log_id}/entries`,
          {
            activity_narrative: entry.activity_narrative,
            category_tags: entry.category_tags,
            entry_time: entry.entry_time,
          }, sendTenantOf(entry)
        );

        const serverId = response.id || response.data?.id || entry.id;
        const oldOfflineId = entry.id;

        // Atomic offline→server id swap so a crash between delete and put can't
        // lose or duplicate the row.
        await db.transaction('rw', db.cachedActivityEntries, async () => {
          await db.cachedActivityEntries.delete(oldOfflineId);
          await db.cachedActivityEntries.put({
            ...entry,
            id: serverId,
            is_synced: true,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            last_error: undefined,
          });
        });
      } else {
        // Backend mounts PATCH /ics214/:id/entries/:entryId, not PUT
        await api.patch(
          `/ics214/${entry.unit_log_id}/entries/${entry.id}`,
          {
            activity_narrative: entry.activity_narrative,
            category_tags: entry.category_tags,
          }, sendTenantOf(entry)
        );

        await db.cachedActivityEntries.update(entry.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }
    } catch (error) {
      await db.cachedActivityEntries.update(entry.id, {
        last_error: (error as Error).message,
      });
      throw error;
    }
  }

  private mapUnitLogToCache(
    log: ICS214UnitLog,
    tenantId: string
  ): CachedUnitLog {
    return {
      id: log.id,
      tenant_id: tenantId,
      incident_id: log.incident_id,
      operational_period_id: log.operational_period_id,
      is_non_incident: log.is_non_incident,
      unit_name: log.unit_name,
      ics_position: log.ics_position,
      home_agency: log.home_agency,
      supervisor_id: log.supervisor_id,
      supervisor_name: log.supervisor_name,
      status: log.status,
      finalized_at: log.finalized_at,
      finalized_by: log.finalized_by,
      handoff_summary: log.handoff_summary,
      entries_count: log.entries_count,
      roster_count: log.roster_count,
      next_actions_count: log.next_actions_count,
      created_at: log.created_at,
      updated_at: log.updated_at,
      is_synced: true,
      synced_at: new Date().toISOString(),
      pending_changes: false,
    };
  }

  private mapActivityEntryToCache(
    entry: ActivityEntry,
    unitLogId: string,
    tenantId: string
  ): CachedActivityEntry {
    return {
      id: entry.id,
      unit_log_id: unitLogId,
      tenant_id: tenantId,
      entry_time: entry.entry_time,
      activity_narrative: entry.activity_narrative,
      category_tags: entry.category_tags || [],
      linked_task_id: entry.linked_task_id,
      created_by_name: entry.created_by_user?.name,
      created_at: entry.created_at,
      updated_at: entry.updated_at,
      is_synced: true,
      synced_at: new Date().toISOString(),
      pending_changes: false,
    };
  }
}

export const ics214OfflineService = new ICS214OfflineService();
